import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { TableCard } from "../shared";
import { onPremLowestScoresColumnDefs, cloudLowestScoresColumnDefs } from "../col_defs";
import useDashboardService from "../services";
import { toast } from "react-toastify";
import eventEmitter from "../../../components/utils/eventEmitter";
import { Box, Drawer, IconButton, Menu, MenuItem, CircularProgress, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import AssetDetailsDrawer from "../../../components/asset/AssetDetailsDrawer";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { authActions } from "../../../store/authSlice";
import { useDispatch } from "react-redux";
import AssetComplianceReportDrawer from "../../../components/asset/AssetComplianceReportDrawer";
import { setConfigDiffData } from "../../../pages/onPremises/ConfigDiff/ConfigDiff-slice";
import {
  SEVERITY_FILTER_OPTIONS as filterOptions,
  DEFAULT_SEVERITY_FILTER as defaultFilter,
} from "../shared/constants";
import AntSwitch from "../../../components/AntSwitch";
import { setSelectedRowID } from "../../../pages/SecurityPosture/TabSelection-slice";
import axiosInstance from "../../../axios/axios";
import { END_POINTS } from "../../../shared";
import { useDashboardContext } from "../DashboardContext";

const PAGE_SIZE = 10;
const SCROLL_THROTTLE_MS = 500;

interface TopMisconfigurationProps {
  isbothCloudAndPremAssetUser: boolean;
  isOnlyPremAssetUser: boolean;
  isOnlyCloudUser: boolean;
}




const OnPremLowestScore = ({ isbothCloudAndPremAssetUser: _isbothCloudAndPremAssetUser, isOnlyPremAssetUser: _isOnlyPremAssetUser, isOnlyCloudUser: _isOnlyCloudUser }: TopMisconfigurationProps) => {
  const { fetchAssetsWithSecurityRisk } = useDashboardService();
  const [tableData, setTableData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isCloud, setIsCloud] = useState(false);
  const [trigger, setTrigger] = useState(0);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const dispatch = useDispatch();
  const menuOpen = Boolean(menuAnchorEl);
  const [reportDrawerOpen, setReportDrawerOpen] = useState(false);
  const [switchDisabled, setSwitchDisabled] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    hasNext: false,
    hasPrevious: false,
  });

  const gridRef = useRef<any>(null);
  const isFetchingNext = useRef(false);
  const isFetchingPrev = useRef(false);
  const isCloudRef = useRef<boolean>(isCloud);
  const lastScrollTs = useRef<number>(0);
  const firstVisibleRowRef = useRef<number>(0);
  const scrollPositionRef = useRef<number>(0);
  const scrollCount = useRef(0); // Count scroll events
  let url = END_POINTS.DEVICE_LIST;
  // Fetch and store assets data on component mount
  useEffect(() => {
    const fetchAssetsData = async () => {
      try {
        const roleReq: any = {
          type: 1,
        };
        const response = await axiosInstance.post(url, roleReq);
        if (response.status === 200) {
          const result = response.data?.data?.device_list || [];

          // Filter and map assets data
          const assetsDetails = result
            .filter((data: any) => {
              return data.device_type !== "Endpoint"; // filter condition
            })
            .map((data: any) => ({
              device_name: data.device_name,
              device_id: data.id,
            }));

          dispatch(
            authActions.updateAssetsList(assetsDetails)
          );
        }
      } catch (error) {
        console.error("Failed to fetch assets data:", error);
      }
    };

    fetchAssetsData();
  }, []);

  // Update ref when isCloud changes
  useEffect(() => {
    isCloudRef.current = isCloud;
  }, [isCloud]);

  const { setIsOverlayOpen } = useDashboardContext();

  // Notify parent context when drawer opens/closes to disable drag and drop
  useEffect(() => {
    setIsOverlayOpen(isDrawerOpen || reportDrawerOpen);
  }, [isDrawerOpen, reportDrawerOpen, setIsOverlayOpen]);

  useEffect(() => {
    if (_isOnlyCloudUser) {
      setIsCloud(true);
      setSwitchDisabled(true);
    } else if (_isOnlyPremAssetUser) {
      setIsCloud(false);
      setSwitchDisabled(true);
    } else {
      setSwitchDisabled(false);
    }
  }, [_isOnlyCloudUser, _isOnlyPremAssetUser]);

  useEffect(() => {
    const handleFilterSaved = () => {
      setLoading(true);
      setTrigger((prev) => prev + 1);
    };
    eventEmitter.on("filterSaved", handleFilterSaved);
    return () => {
      eventEmitter.off("filterSaved", handleFilterSaved);
    };
  }, []);

  useEffect(() => {
    if (isCloud || trigger || (_isbothCloudAndPremAssetUser || _isOnlyPremAssetUser)) {
      scrollCount.current = 0; // Reset scroll counter
      isFetchingNext.current = false; // Reset fetching flags
      isFetchingPrev.current = false;

      fetchData({ page: 1, mode: "initial" });
    }
  }, [trigger, isCloud]);

  // Memoize columnDefs to avoid unnecessary recalculation
  const columnDefs = useMemo(
    () => (isCloud ? cloudLowestScoresColumnDefs : onPremLowestScoresColumnDefs),
    [isCloud]
  );

  const fetchData = useCallback(
    async ({
      page = 1,
      mode = "initial",
    }: {
      page?: number;
      mode?: "initial" | "append" | "prepend";
    } = {}) => {
      const currentIsCloud = isCloudRef.current;

      // Capture pre-fetch scroll state
      try {
        const apiBefore = gridRef.current?.api;
        if (apiBefore && mode !== "initial") {
          firstVisibleRowRef.current = typeof apiBefore.getFirstDisplayedRow === "function" ? apiBefore.getFirstDisplayedRow() : firstVisibleRowRef.current;
          const vRangeBefore = apiBefore.getVerticalPixelRange?.();
          scrollPositionRef.current = vRangeBefore?.top ?? scrollPositionRef.current;
        }
      } catch (err) {
        console.warn("Could not capture pre-fetch scroll state for OnPremLowestScore", err);
      }

      if (mode === "initial") {
        setLoading(true);
      } else {
        setIsFetchingMore(true);
      }

      try {
        const module = currentIsCloud ? "cloud" : "on_premise";

        const {
          data,
          pagination: pageInfo,
          error,
        } = await fetchAssetsWithSecurityRisk(module, {
          page,
          page_size: PAGE_SIZE,
        });

        console.log("📥 API Response:", {
          module,
          page,
          dataType: Array.isArray(data) ? "array" : typeof data,
          dataLength: Array.isArray(data) ? data.length : "N/A",
          dataSample: Array.isArray(data) && data.length > 0 ? data[0] : data,
          pageInfo,
          error,
        });

        if (error) {
          toast.error((error as any)?.message || "Failed to load assets with security risk");
          return;
        }

        setPagination({
          page: pageInfo?.page || page,
          totalPages: pageInfo?.total_pages || 1,
          hasNext: pageInfo?.has_next || false,
          hasPrevious: pageInfo?.has_previous || false,
        });

        // Format data with failed_count
        const formattedData = (data || []).map((item: any) => ({
          ...item,
          failed_count: item.vulnerability_levels?.failed_count ?? 0,
        }));

        setTableData((prev: any) => {
          if (mode === "initial") {
            return formattedData;
          } else if (mode === "append") {
            return [...prev, ...formattedData];
          } else if (mode === "prepend") {
            return [...formattedData, ...prev];
          }
          return formattedData;
        });

        // Restore scroll position after DOM updates
        try {
          const apiAfter = gridRef.current?.api;
          if (apiAfter) {
            if (mode === "append") {
              const indexToShow = firstVisibleRowRef.current ?? 0;
              setTimeout(() => apiAfter.ensureIndexVisible?.(indexToShow, "top"), 50);
            } else if (mode === "prepend") {
              const indexToShow = (firstVisibleRowRef.current ?? 0) + (formattedData?.length || 0);
              setTimeout(() => apiAfter.ensureIndexVisible?.(indexToShow, "top"), 50);
            }
          }
        } catch (err) {
          console.warn("Could not restore scroll for OnPremLowestScore", err);
        }
      } catch (error: any) {
        toast.error(error?.message || "Failed to fetch assets data");
      } finally {
        setLoading(false);
        setIsFetchingMore(false);
        isFetchingNext.current = false;
        isFetchingPrev.current = false;
      }
    },
    []
  );



  // Throttled infinite scroll handler - FIXED: use ref for pagination to avoid recreating
  const paginationRef = useRef(pagination);
  paginationRef.current = pagination;

  const handleScrollEnd = useCallback(() => {
    // SIMPLE FIX: Ignore the first scroll event (AG Grid auto-scroll on mount)
    scrollCount.current++;
    if (scrollCount.current === 1) {
      console.log("🚫 Ignored first scroll event (AG Grid auto-scroll)");
      return;
    }

    const now = Date.now();
    if (now - lastScrollTs.current < SCROLL_THROTTLE_MS) {
      return;
    }
    lastScrollTs.current = now;

    const gridApi = gridRef.current?.api;
    if (!gridApi) {
      return;
    }

    try {
      // Get the total row count from the data
      const totalRowCount = tableData.length;
      if (totalRowCount === 0) return;

      // Get viewport info from the event or API
      const verticalPixelRange = gridApi.getVerticalPixelRange?.();

      if (!verticalPixelRange) {
        // Fallback: use simple row count check
        const currentPagination = paginationRef.current;

        // If we have all data loaded and user scrolled, check if we need more
        if (currentPagination.hasNext && !isFetchingNext.current) {
          console.log("⬇️ Loading next page:", currentPagination.page + 1);
          isFetchingNext.current = true;
          fetchData({ page: currentPagination.page + 1, mode: "append" });
        }
        return;
      }

      // Calculate scroll position
      const { top, bottom } = verticalPixelRange;
      const scrollTop = top;
      const scrollHeight =
        gridApi.getModel?.()?.getRowCount?.() * 50 || totalRowCount * 50; // Assume 50px row height
      const clientHeight = bottom - top;

      const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;
      const isNearBottom = scrollPercentage > 0.8; // 80% scrolled
      const isNearTop = scrollTop < 100; // Less than 100px from top

      const currentPagination = paginationRef.current;

      console.log("📜 Scroll:", {
        scrollTop,
        scrollHeight,
        clientHeight,
        scrollPercentage: (scrollPercentage * 100).toFixed(1) + "%",
        isNearBottom,
        isNearTop,
        hasNext: currentPagination.hasNext,
        hasPrev: currentPagination.hasPrevious,
      });

      // Load next page when near bottom
      if (
        isNearBottom &&
        currentPagination.hasNext &&
        !isFetchingNext.current
      ) {
        console.log("⬇️ Loading next page:", currentPagination.page + 1);
        isFetchingNext.current = true;
        fetchData({ page: currentPagination.page + 1, mode: "append" });
        return;
      }

      // Load previous page when near top
      if (
        isNearTop &&
        currentPagination.hasPrevious &&
        !isFetchingPrev.current
      ) {
        console.log("⬆️ Loading previous page:", currentPagination.page - 1);
        isFetchingPrev.current = true;
        fetchData({ page: currentPagination.page - 1, mode: "prepend" });
        return;
      }
    } catch (err) {
      console.error("Scroll handler error:", err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData.length]);

  const memoizedGridOptions = useMemo(
    () => ({
      rowHeight: 50,
      domLayout: "normal",
      suppressScrollOnNewData: true,
      onBodyScrollEnd: handleScrollEnd,
      fullWidthCellRenderer: () => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            width: '100%',
          }}
        >
          <CircularProgress size={24} />
        </Box>
      ),
      isFullWidthRow: (params: any) => params.rowNode?.data?._isLoadingRow === true,
    }),
    [handleScrollEnd]
  );

  // Add loading row at the end when fetching more pages
  // Note: failed_count is set to Infinity so the loading row sorts to the bottom
  // when the Misconfiguration column has sort: "asc" applied
  const displayData = useMemo(() => {
    if (isFetchingMore) {
      return [...tableData, { _isLoadingRow: true, failed_count: Infinity }];
    }
    return tableData;
  }, [tableData, isFetchingMore]);
  const onRowClick = (data: any) => {
    console.log("Row clicked:", data);
    dispatch(
      authActions.setAssetsData({
        tempAssetName: data.device_name,
      })
    );
    dispatch(setSelectedRowID(data.id));
    setSelectedRow(data);
    setIsDrawerOpen(true);

    const shouldShowMenu =
      data.device_type?.toLowerCase() !== "endpoint";

    setShowMenu(shouldShowMenu);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    setSelectedRow(null);
  };
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  const navigate = useNavigate();
  const handleAssetComplianceReport = () => {
    setIsDrawerOpen(false);
    setReportDrawerOpen(true);
  };

  const handleMenuKebabClose = () => {
    setMenuAnchorEl(null);
  };

  const handleShowConfig = (deviceId: any, selectDevice: any = null) => {
    dispatch(
      setConfigDiffData({
        rowData: selectDevice,
        device_id: String(deviceId),

        click: "assets",
      })

    );
    navigate(`/on-premises/configdiff`, {
      state: { device_id: String(deviceId) }
    });
  };

  const handleMenuAction = (action: string) => {
    switch (action) {
      case "ShowConfigDiff":
        if (selectedRow?.id) {
          handleShowConfig(selectedRow.id, selectedRow);
        }
        break;
    }
    handleMenuKebabClose();
  };

  const handleMenuKebabClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };
  const [showMenu, setShowMenu] = useState(true);
  const MenuKebabDrawer = (
    <Box sx={{ display: "flex", justifyContent: "center" }}>
      <IconButton
        size="small"
        onClick={handleMenuKebabClick}
        sx={{
          color: "text.secondary",
          "&:hover": {
            backgroundColor: "action.hover",
          },
        }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={menuAnchorEl}
        open={menuOpen}
        onClose={handleMenuKebabClose}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{
          sx: {
            width: 200,
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
          },
        }}
      >
        {/* {hasPermissions(ROLES_CONTENT_PROPS.DECOMMISSION_DEVICE) && (
            <MenuItem onClick={() => handleMenuAction("DeCommission")}>
              Decommission
            </MenuItem>
          )} */}
        <MenuItem onClick={() => handleMenuAction("ShowConfigDiff")}>
          Show Config Diff
        </MenuItem>
      </Menu>
    </Box>
  );

  return (
    <div>
      <TableCard
        title={
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
            <div>
              {isCloud ? "Resources With Security Risk" : "Assets With Security Risk"}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span>On-Prem</span>
              <AntSwitch
                checked={isCloud}
                onChange={(_, checked) => {
                  setIsCloud(checked);
                  setPagination({
                    page: 1,
                    totalPages: 1,
                    hasNext: false,
                    hasPrevious: false,
                  });
                }}
                disabled={switchDisabled}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "#FFFFFF",
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#3A2ADA",
                  },
                  "& .MuiSwitch-track": {
                    backgroundColor: "#3A2ADA",
                  },
                }}
              />
              <span>Cloud</span>
            </div>
          </div>
        }
        columnDefs={columnDefs}
        rowData={displayData}
        showSearch={false}
        isRowSelectable={false}
        loading={loading}
        onRowClick={(data: any) => {
          // Don't trigger row click for loading row
          if (!data._isLoadingRow) {
            onRowClick(data);
          }
        }}
        showFilter={true}
        sortOptions={filterOptions}
        gridRef={gridRef}
        gridOptions={memoizedGridOptions}
      />
      {/* Loading indicator for pagination
      {isFetchingMore && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            py: 1,
          }}
        >
          <CircularProgress size={18} />
        </Box>
      )} */}
      <AssetDetailsDrawer
        open={isDrawerOpen}
        onClose={handleDrawerClose}
        assetData={selectedRow}
        isCloud={isCloud}
        isMenu={!isCloud && showMenu ? MenuKebabDrawer : undefined}
        onReport={!isCloud ? handleAssetComplianceReport : undefined}
        isCve={!isCloud}
      />
      <Drawer
        anchor="right"
        open={reportDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(true);
          setReportDrawerOpen(false);
        }}
        PaperProps={{ sx: { width: 400, borderRadius: 2 } }}
      >
        <AssetComplianceReportDrawer
          onClose={() => {
            setIsDrawerOpen(true);
            setReportDrawerOpen(false);
          }}
          assetData={selectedRow}
        />
      </Drawer>
    </div>
  );
};

export default OnPremLowestScore;
